/* eslint-disable import/prefer-default-export */
import { useRef, useMemo, useEffect } from "react";
import type { AnyObject, ObjectSchema } from "yup";
import useLatestRef from "utils/useLatestRef";
import { AppliedFilter } from "components/Shared/FilterEditor/utils";
import {
  SEARCH_FILTER_ID,
  SEARCH_FILTER_VARIANT,
} from "components/Shared/FilterEditor/standardFilters";

type UseValidateInitialDataOptions<
  D extends AnyObject,
  S extends AnyObject = D,
> = {
  initialData: D;
  schema: ObjectSchema<S>;
  onInvalid: () => void;
};

export function useValidateInitialData<
  D extends AnyObject,
  S extends AnyObject = D,
>({ initialData, schema, onInvalid }: UseValidateInitialDataOptions<D, S>) {
  const initialFiltersRef = useRef(initialData);

  const isInitialValid = useMemo(() => {
    const initialFilters = initialFiltersRef.current;
    if (!initialFilters) return true;
    return schema.isValidSync(initialFilters);
  }, [schema]);

  const onInvalidLatestRef = useLatestRef(onInvalid);

  useEffect(() => {
    if (!isInitialValid) onInvalidLatestRef.current();
  }, [isInitialValid, onInvalidLatestRef]);

  return isInitialValid;
}

export function getAppliedSearchFilter(search: string): AppliedFilter {
  return {
    filter: SEARCH_FILTER_ID,
    variant: SEARCH_FILTER_VARIANT,
    value: search,
  };
}
